<template>
  <div
    class="mt-4 mb-6 grid grid-cols-12 items-center gap-x-4 gap-y-2 rounded-md"
  >
    <div class="col-span-12 flex gap-x-4 lg:col-span-4">
      <div class="grow">
        <label for="search" class="sr-only text-sm font-medium text-gray-700">{{
          t('search')
        }}</label>
        <div class="flex rounded-md shadow-xs">
          <div class="relative grow self-center focus-within:z-10">
            <div
              class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3"
            >
              <MagnifyingGlassIcon
                class="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </div>
            <input
              id="search"
              ref="searchInput"
              v-model="vacancyListStore.filter.vacancy"
              type="search"
              name="search"
              class="block w-full rounded-md border-gray-300 pl-10 text-sm focus:border-indigo-500 focus:ring-indigo-500"
              :placeholder="t('vacancies.search.title')"
            />
            <div
              v-if="!vacancyListStore.filter.vacancy"
              class="absolute inset-y-0 right-0 hidden py-1.5 pr-1.5 sm:block"
            >
              <kbd
                class="inline-flex items-center rounded-sm border border-gray-200 px-2 font-sans text-sm font-medium text-gray-400"
              >
                Ctrl /
              </kbd>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-span-12 lg:col-span-4">
      <div class="flex grow flex-col gap-x-2 lg:flex-row lg:items-center">
        <label for="sites" class="text-sm font-medium text-gray-700">{{
          t('jobAlerts.search.site')
        }}</label>

        <Multiselect
          id="sites"
          ref="multiselect"
          v-model="site"
          :options="searchSite"
          :filterResults="false"
          :noOptionsText="t('vacancy.company.noOptions')"
          :placeholder="t('jobAlerts.search.sitePlaceholder')"
          :resolveOnLoad="true"
          :minChars="0"
          :delay="500"
          :searchable="true"
          :object="true"
          valueProp="id"
          label="name"
          trackBy="name"
          inputType="search"
          autocomplete="off"
          class="block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
        />
      </div>
    </div>

    <div class="col-span-12 lg:col-span-4">
      <div class="flex grow flex-col gap-x-2 lg:flex-row lg:items-center">
        <label for="company" class="block text-sm font-medium text-gray-700">{{
          t('jobAlerts.search.company')
        }}</label>
        <Multiselect
          id="company"
          v-model="company"
          class="block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          :placeholder="t('vacancy.company.placeholder')"
          :minChars="0"
          :filterResults="false"
          :resolveOnLoad="true"
          :delay="500"
          :searchable="true"
          :options="searchCompany"
          :object="true"
          :noOptionsText="t('vacancy.company.noOptions')"
          valueProp="id"
          label="name"
          trackBy="name"
          inputType="search"
          autocomplete="off"
        />
      </div>
    </div>

    <div class="col-span-12 lg:col-span-3">
      <div class="flex grow flex-col gap-x-2 lg:flex-row lg:items-center">
        <label for="publishers" class="text-sm font-medium text-gray-700">{{
          t('vacancies.search.publisher')
        }}</label>

        <Multiselect
          id="publishers"
          ref="multiselect"
          v-model="publisher"
          :options="searchPublisher"
          :filterResults="false"
          :noOptionsText="t('vacancy.company.noOptions')"
          :placeholder="t('vacancies.search.publisherPlaceholder')"
          :resolveOnLoad="true"
          :minChars="0"
          :delay="500"
          :searchable="true"
          :object="true"
          valueProp="id"
          label="name"
          trackBy="name"
          inputType="search"
          autocomplete="off"
          class="block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
        />
      </div>
    </div>

    <div
      class="col-span-9 flex flex-col items-start gap-x-2 lg:col-span-3 lg:flex-row lg:items-center"
    >
      <label
        for="startsAfter"
        class="block shrink-0 text-sm font-medium text-gray-700"
        >{{ t('vacancies.search.publishedAfter') }}</label
      >

      <DatePicker
        :value="vacancyListStore.filter.publication.startsAfter"
        :inputAttr="{ id: 'startsAfter' }"
        type="dateTime"
        format="D MMM YYYY"
        inputClass="focus:border-indigo-500 border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 w-full block rounded-md shadow-xs focus:outline-hidden sm:text-sm"
        :clearable="true"
        :editable="false"
        class="mt-1"
        @change="(value) => updateDateRange('startsAfter', value)"
      />
    </div>
    <div
      class="col-span-9 flex flex-col items-start gap-x-2 lg:col-span-3 lg:flex-row lg:items-center"
    >
      <label
        for="endsBefore"
        class="block shrink-0 text-sm font-medium text-gray-700"
        >{{ t('vacancies.search.unpublishedBefore') }}</label
      >

      <DatePicker
        :value="vacancyListStore.filter.publication.endsBefore"
        :inputAttr="{ id: 'endsBefore' }"
        type="dateTime"
        format="D MMM YYYY"
        inputClass="focus:border-indigo-500 border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 w-full block rounded-md shadow-xs focus:outline-hidden sm:text-sm"
        :clearable="true"
        :editable="false"
        class="mt-1"
        @change="(value) => updateDateRange('endsBefore', value)"
      />
    </div>
    <div class="col-span-12 flex gap-x-4 lg:col-span-2">
      <Listbox
        v-model="publicationScope"
        as="div"
        class="flex w-full flex-col gap-x-2 lg:flex-row lg:items-center"
      >
        <ListboxLabel class="block shrink-0 text-sm font-medium text-gray-700">
          {{ t('vacancies.search.publicationScope') }}
        </ListboxLabel>
        <div class="relative md:w-full">
          <ListboxButton
            class="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-2 pr-10 pl-3 text-left shadow-xs focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-hidden sm:text-sm"
          >
            <span class="ml-2 block truncate capitalize">{{
              publicationScope?.toLowerCase() || t('candidates.filters.all')
            }}</span>
            <span
              class="pointer-events-none absolute inset-y-0 right-0 ml-2 flex items-center pr-2"
            >
              <ChevronUpDownIcon
                class="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </span>
          </ListboxButton>

          <transition
            leaveActiveClass="transition ease-in duration-100"
            leaveFromClass="opacity-100"
            leaveToClass="opacity-0"
          >
            <ListboxOptions
              class="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm"
            >
              <ListboxOption
                v-for="(scope, index) in scopes"
                :key="index"
                v-slot="{ active, selected }"
                as="template"
                :value="scope"
              >
                <li
                  :class="[
                    active ? 'bg-indigo-600 text-white' : 'text-gray-900',
                    'relative cursor-default py-2 pr-9 pl-3 select-none',
                  ]"
                >
                  <span
                    :class="[
                      selected ? 'font-semibold' : 'font-normal',
                      'block truncate capitalize',
                    ]"
                  >
                    {{ scope?.toLowerCase() || t('candidates.filters.all') }}
                  </span>
                </li>
              </ListboxOption>
            </ListboxOptions>
          </transition>
        </div>
      </Listbox>
    </div>
    <div class="col-span-1 mt-1 ml-auto">
      <button
        type="button"
        class="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-xs hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:outline-hidden disabled:bg-indigo-400 sm:w-auto"
        :disabled="vacancyListStore.isLoading"
        @click="showExtraParams = !showExtraParams"
      >
        {{
          showExtraParams
            ? t('vacancies.search.hideAdvancedSearch')
            : t('vacancies.search.advancedSearch')
        }}
        <LoadingSpinner
          v-if="vacancyListStore.isLoading"
          class="ml-2 h-4 w-4"
        />
      </button>
    </div>

    <template v-if="showExtraParams">
      <div class="col-span-12 my-2 border border-dashed border-gray-300"></div>

      <div class="col-span-12 flex items-center lg:col-span-4">
        <Combobox v-model="selectedLocation" nullable name="location">
          <ComboboxLabel
            class="mr-2 block shrink-0 text-sm font-medium text-gray-700"
            >{{ t('vacancies.search.location') }}</ComboboxLabel
          >
          <div class="relative w-full grow">
            <ComboboxInput
              class="relative w-full cursor-default rounded-l-md border border-gray-300 bg-white py-2 pr-10 pl-3 text-left shadow-xs focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-hidden sm:text-sm"
              :displayValue="(location) => locationLabel(location)"
              :placeholder="t('vacancies.search.locationPlaceholder')"
              @change="locationQuery = $event.target.value"
            />
            <ComboboxButton
              class="absolute inset-y-0 right-0 flex items-center pr-2"
            >
              <ChevronUpDownIcon
                class="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </ComboboxButton>
            <TransitionRoot
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
              @after-leave="locationQuery = ''"
            >
              <ComboboxOptions
                v-show="locationQuery !== ''"
                class="absolute z-1 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg focus:outline-2 sm:text-sm"
              >
                <div
                  v-if="filteredLocations.length === 0 && locationQuery !== ''"
                  class="relative cursor-default px-4 py-2 text-gray-700 select-none"
                >
                  {{ t('vacancies.search.noLocations') }}
                </div>

                <ComboboxOption
                  v-for="location in filteredLocations"
                  :key="location.place_id"
                  v-slot="{ selected, active }"
                  as="template"
                  :value="location"
                >
                  <li
                    class="location-option relative cursor-default py-2 pr-4 pl-10 text-gray-900 select-none"
                    :class="active ? `active` : 'inactive'"
                  >
                    <span
                      class="block truncate"
                      :class="{
                        'font-medium': selected,
                        'font-normal': !selected,
                      }"
                    >
                      {{ locationLabel(location) }}
                    </span>
                    <span
                      v-if="selected"
                      class="selected absolute inset-y-0 left-0 flex items-center pl-3"
                    >
                      <CheckIcon class="h-5 w-5" aria-hidden="true" />
                    </span>
                  </li>
                </ComboboxOption>
              </ComboboxOptions>
            </TransitionRoot>
          </div>
        </Combobox>

        <Listbox
          v-model="vacancyListStore.filter.geoLocation.distance"
          as="div"
          class="flex flex-col gap-x-2 lg:flex-row lg:items-center"
          @update:model-value="
            vacancyListStore.filter.geoLocation.latitude && loadVacancies()
          "
        >
          <ListboxLabel
            class="sr-only block shrink-0 text-sm font-medium text-gray-700"
          >
            {{ t('vacancies.search.distance') }}
          </ListboxLabel>
          <div class="relative md:w-28">
            <ListboxButton
              class="relative w-full cursor-default rounded-r-md border border-gray-300 bg-white py-2 pr-10 pl-3 text-left shadow-xs focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-hidden sm:text-sm"
            >
              <span class="ml-2 block truncate capitalize"
                >+{{ vacancyListStore.filter.geoLocation.distance }}km</span
              >
              <span
                class="pointer-events-none absolute inset-y-0 right-0 ml-2 flex items-center pr-2"
              >
                <ChevronUpDownIcon
                  class="h-5 w-5 text-gray-400"
                  aria-hidden="true"
                />
              </span>
            </ListboxButton>

            <transition
              leaveActiveClass="transition ease-in duration-100"
              leaveFromClass="opacity-100"
              leaveToClass="opacity-0"
            >
              <ListboxOptions
                class="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm"
              >
                <ListboxOption
                  v-for="distance in [1, 5, 10, 20, 30, 50]"
                  :key="distance"
                  v-slot="{ active, selected }"
                  as="template"
                  :value="distance"
                >
                  <li
                    :class="[
                      active ? 'bg-indigo-600 text-white' : 'text-gray-900',
                      'relative cursor-default py-2 pr-9 pl-3 select-none',
                    ]"
                  >
                    <span
                      :class="[
                        selected ? 'font-semibold' : 'font-normal',
                        'block truncate capitalize',
                      ]"
                    >
                      +{{ distance }}km
                    </span>
                  </li>
                </ListboxOption>
              </ListboxOptions>
            </transition>
          </div>
        </Listbox>
      </div>

      <div class="col-span-9 ml-1 flex gap-x-4 lg:col-span-4">
        <div class="flex flex-col gap-x-2 lg:flex-row lg:items-center">
          <label
            for="salaryMin"
            class="shrink-0 text-sm font-medium text-gray-700"
            >{{ t('vacancies.search.salaryMin') }}</label
          >
          <div class="flex rounded-md shadow-xs">
            <div class="relative self-center focus-within:z-10">
              <input
                id="salaryMin"
                ref="searchInput"
                v-model="vacancyListStore.filter.salaryMin"
                type="number"
                min="0"
                step="500"
                name="salaryMin"
                class="block w-full rounded-md border-gray-300 text-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-x-2 lg:flex-row lg:items-center">
          <label
            for="salaryMax"
            class="shrink-0 text-sm font-medium text-gray-700"
            >{{ t('vacancies.search.salaryMax') }}</label
          >
          <div class="flex rounded-md shadow-xs">
            <div class="relative self-center focus-within:z-10">
              <input
                id="salaryMax"
                ref="searchInput"
                v-model="vacancyListStore.filter.salaryMax"
                type="number"
                min="0"
                step="500"
                name="salaryMin"
                class="block w-full rounded-md border-gray-300 text-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="col-span-12 flex gap-x-4 lg:col-span-4">
        <SwitchGroup>
          <div class="flex items-center">
            <Switch
              v-model="topItem"
              :class="topItem ? 'bg-indigo-600' : 'bg-gray-300'"
              class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:outline-none"
            >
              <span
                :class="topItem ? 'translate-x-6' : 'translate-x-1'"
                class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform"
              ></span>
            </Switch>
            <SwitchLabel class="ml-2 text-sm font-medium text-gray-700"
              >{{ t('vacancies.search.topJobs') }}
            </SwitchLabel>
          </div>
        </SwitchGroup>
        <SwitchGroup>
          <div class="flex items-center">
            <Switch
              v-model="staffingPost"
              :class="staffingPost ? 'bg-indigo-600' : 'bg-gray-300'"
              class="relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:outline-none"
            >
              <span
                :class="staffingPost ? 'translate-x-6' : 'translate-x-1'"
                class="inline-block h-4 w-4 transform rounded-full bg-white transition-transform"
              ></span>
            </Switch>
            <SwitchLabel class="ml-2 text-sm font-medium text-gray-700"
              >{{ t('vacancies.search.staffingJobs') }}
            </SwitchLabel>
          </div>
        </SwitchGroup>
      </div>

      <div
        v-for="(group, key) in siteTagGroups"
        :key="key"
        class="col-span-12 lg:col-span-4"
      >
        <div class="flex grow flex-col gap-x-2 lg:flex-row lg:items-center">
          <label :for="key" class="text-sm font-medium text-gray-700">
            {{ t(`jobAlerts.search.${key}`) }}
          </label>
          <Multiselect
            :id="key"
            :maxHeight="400"
            :modelValue="selectedTags[key]"
            :options="group"
            :searchable="true"
            :object="true"
            valueProp="key"
            label="label"
            trackBy="label"
            class="block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            mode="tags"
            @change="(e) => updateTags(e, key)"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import Multiselect from '@vueform/multiselect'
import DatePicker from 'vue-datepicker-next'
import {
  CheckIcon,
  ChevronUpDownIcon,
  MagnifyingGlassIcon,
} from '@heroicons/vue/20/solid'
import { useVacancyListStore } from '../../store/vacancyListStore'
import { useI18n } from 'vue-i18n'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import tooManagement from '../../api/tooManagement'
import { watchDebounced } from '@vueuse/core'
import LoadingSpinner from '../LoadingSpinner.vue'
import { endOfDay, startOfDay } from 'date-fns'
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxLabel,
  ComboboxOption,
  ComboboxOptions,
  Listbox,
  ListboxButton,
  ListboxLabel,
  ListboxOption,
  ListboxOptions,
  Switch,
  SwitchGroup,
  SwitchLabel,
  TransitionRoot,
} from '@headlessui/vue'
import { locationIQAPIKey } from '../../utils/location'
import { useSettingsStore } from '../../store/settingsStore'

const vacancyListStore = useVacancyListStore()
const settingsStore = useSettingsStore()
const { t } = useI18n({
  inheritLocale: true,
  useScope: 'global',
})

const showExtraParams = ref(!vacancyListStore.isDefaultSearch)

const site = ref(
  vacancyListStore.filter.siteIds.length
    ? vacancyListStore.filter.siteIds[0]
    : {},
)
const searchSite = async (filter = '') => {
  const queryRes = await tooManagement.getSites({
    filter: filter || '',
    size: 15,
  })
  return queryRes.data
}

const company = ref(
  vacancyListStore.filter.companyIds.length
    ? vacancyListStore.filter.companyIds[0]
    : {},
)

const topItem = computed({
  get() {
    return vacancyListStore.filter.topItem
  },
  set(newValue) {
    if (!newValue) {
      vacancyListStore.filter.topItem = null // We need to use null instead of false to get all the vacancies
    } else {
      vacancyListStore.filter.topItem = true
    }
    loadVacancies()
  },
})

const staffingPost = computed({
  get() {
    return vacancyListStore.filter.staffingPost
  },
  set(newValue) {
    if (!newValue) {
      vacancyListStore.filter.staffingPost = null // We need to use null instead of false to get all the vacancies
    } else {
      vacancyListStore.filter.staffingPost = true
    }
    loadVacancies()
  },
})

const searchCompany = async (filter = '') => {
  const queryRes = await tooManagement.getCompanies({
    filter: filter || '',
    size: 15,
  })
  return queryRes.data
}

if (settingsStore.activePublisher?.id) {
  vacancyListStore.filter.publisherIds = [settingsStore.activePublisher]
}

const publisher = ref(
  vacancyListStore.filter.publisherIds.length
    ? vacancyListStore.filter.publisherIds[0]
    : {},
)

const searchPublisher = async (filter = '') => {
  const queryRes = await tooManagement.getPublishers({
    filter: filter || '',
    size: 15,
  })
  return queryRes.data
}

const updateDateRange = (fieldName, value) => {
  if (fieldName === 'endsBefore' && value) {
    vacancyListStore.filter.publication[fieldName] = endOfDay(value)
  } else if (fieldName === 'startsAfter' && value) {
    vacancyListStore.filter.publication[fieldName] = startOfDay(value)
  } else {
    vacancyListStore.filter.publication[fieldName] = null
  }

  loadVacancies()
}

const scopes = [null, 'EXTERNAL', 'INTERNAL', 'REGIONAL']
const publicationScope = ref(
  vacancyListStore.filter.publication.scopes.length === 1
    ? vacancyListStore.filter.publication.scopes[0]
    : null,
)

const selectedLocation = ref(vacancyListStore.filter.geoLocation.label)
const locationQuery = ref('')
const filteredLocations = ref([])

function locationLabel(location) {
  if (location?.display_place) {
    return `${location?.display_place || location}${location?.type === 'state' ? ' (Provincie)' : ''}`
  }
  return location || ''
}

const siteTagGroups = ref({})
const selectedTags = ref({
  VACANCY_EDUCATION: vacancyListStore.filter.education,
  VACANCY_WORKFIELD: vacancyListStore.filter.discipline,
  VACANCY_WORKCATEGORY: vacancyListStore.filter.workCategory,
  VACANCY_WORKEXPERIENCE: vacancyListStore.filter.workExperience,
  VACANCY_EMPLOYMENTTYPE: vacancyListStore.filter.employmentType,
  REGION: vacancyListStore.filter.region,
})
const updateTags = (tags, group) => {
  selectedTags.value[group] = tags

  let tagField
  switch (group) {
    case 'VACANCY_EDUCATION':
      tagField = 'education'
      break
    case 'VACANCY_WORKFIELD':
      tagField = 'discipline'
      break
    case 'VACANCY_WORKCATEGORY':
      tagField = 'workCategory'
      break
    case 'VACANCY_WORKEXPERIENCE':
      tagField = 'workExperience'
      break
    case 'VACANCY_EMPLOYMENTTYPE':
      tagField = 'employmentType'
      break
    case 'REGION':
      tagField = 'region'
      break
  }
  vacancyListStore.filter[tagField] = tags
  loadVacancies()
}

const fetchSiteTags = async (site) => {
  const [tagGroupsResponse, regionsResponse] = await Promise.all([
    tooManagement.getTagGroups(site.id),
    tooManagement.getSiteRegions(site.id),
  ])
  const tagGroupPromises = tagGroupsResponse.data.map((tagGroup) =>
    tooManagement.getTags(tagGroup.id),
  )
  if (regionsResponse.data?.length) {
    siteTagGroups.value['REGION'] = regionsResponse.data.map((region) => ({
      key: region.slug,
      label: region.name,
    }))
  }

  const tags = await Promise.all(tagGroupPromises)
  tags.forEach((tagGroup, index) => {
    if (tagGroup.data.length) {
      siteTagGroups.value[tagGroupsResponse.data[index].tagCategory] =
        tagGroup.data
    }
  })
}

const loadVacancies = async () => {
  vacancyListStore.page.number = 1
  await vacancyListStore.load()
}

watchDebounced(
  [
    () => vacancyListStore.filter.vacancy,
    () => vacancyListStore.filter.salaryMin,
    () => vacancyListStore.filter.salaryMax,
  ],
  () => {
    loadVacancies()
  },
  { debounce: 500 },
)

watch(
  site,
  async (newSite) => {
    vacancyListStore.filter.siteIds = newSite?.id ? [newSite] : []
    loadVacancies()

    if (newSite?.id) {
      fetchSiteTags(newSite)
    } else {
      siteTagGroups.value = {}
      selectedTags.value = {
        VACANCY_EDUCATION: [],
        VACANCY_WORKFIELD: [],
        VACANCY_WORKCATEGORY: [],
        VACANCY_WORKEXPERIENCE: [],
        VACANCY_EMPLOYMENTTYPE: [],
        REGION: [],
      }
    }
  },
  {
    immediate: true,
  },
)

watch(company, (newCompany) => {
  vacancyListStore.filter.companyIds = newCompany?.id ? [newCompany] : []
  loadVacancies()
})

watch(publisher, (newPublisher) => {
  vacancyListStore.filter.publisherIds = newPublisher?.id ? [newPublisher] : []
  loadVacancies()
})

watch(publicationScope, (newScope) => {
  vacancyListStore.filter.publication.scopes = newScope ? [newScope] : []
  if (
    vacancyListStore.filter.publication.startsAfter ||
    vacancyListStore.filter.publication.endsBefore
  ) {
    loadVacancies()
  }
})

watch(
  () => settingsStore.activePublisher,
  (newPublisher) => {
    publisher.value = newPublisher
  },
)

watch(selectedLocation, (newLocation) => {
  if (newLocation?.lat) {
    vacancyListStore.filter.geoLocation.latitude = parseFloat(newLocation.lat)
    vacancyListStore.filter.geoLocation.longitude = parseFloat(newLocation.lon)
    vacancyListStore.filter.geoLocation.label = newLocation.display_place
  } else {
    vacancyListStore.filter.geoLocation.latitude = null
    vacancyListStore.filter.geoLocation.longitude = null
    vacancyListStore.filter.geoLocation.label = ''
  }

  loadVacancies()
})

watchDebounced(
  locationQuery,
  async () => {
    filteredLocations.value = []
    if (locationQuery.value) {
      const response = await fetch(
        `https://api.locationiq.com/v1/autocomplete?q=${locationQuery.value}&key=${locationIQAPIKey}&&countrycodes=nl&accept-language=nl&tag=place:city,place:town,place:borough,place:municipality,place:village,place:hamlet,place:state`,
      )
      if (!response.ok) {
        return
      }
      const data = await response.json()
      if (data && data.length) {
        filteredLocations.value = data
      }
    }
  },
  { debounce: 500, maxWait: 5000 },
)

const searchInput = ref(null)
const onKeydown = (event) => {
  if (event.key === '/' && event.ctrlKey) {
    searchInput.value?.focus()
  }
}
onMounted(() => {
  window.addEventListener('keydown', onKeydown)
})
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
})
</script>
